/**
 * MacroCalendarPanel - 2026-06-07 r-batch.
 *
 * Replaces the legacy blank-dash Economic Calendar block. Renders:
 *   - Top summary bar (regime / next major / highest impact / freshness)
 *   - Filter chips (Today / This Week / High Impact / Fed / Inflation / Jobs / Energy)
 *   - Main events table (Time / Event / Country / Impact / Actual /
 *     Forecast / Previous / Strategy relevance)
 *   - Refresh Macro Data button (real fetch)
 *   - Honest empty/unavailable state
 *
 * Source: /api/v1/market/macro-calendar (read-only adapter over the
 * existing market_pulse.service calendar payload + macro_context regime).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar, RefreshCw, AlertCircle, Activity } from 'lucide-react';
import { apiV1Base } from '../lib/apiConfig';
import { safeFormatTimestamp } from '../lib/accountFormatters';

interface MacroEvent {
  time_utc: string | null;
  event: string | null;
  country: string | null;
  impact: string | null;
  actual: string | number | null;
  forecast: string | number | null;
  previous: string | number | null;
  strategy_relevance: string | null;
}

interface MacroPayload {
  status: 'live' | 'limited' | 'unavailable' | string;
  source: string | null;
  last_updated_utc: string | null;
  regime: string | null;
  next_major_event: MacroEvent | null;
  highest_impact_event: MacroEvent | null;
  events: MacroEvent[];
  reason_code?: string | null;
}

const FILTERS = [
  { key: 'today', label: 'Today' },
  { key: 'this_week', label: 'This Week' },
  { key: 'high_impact', label: 'High Impact' },
  { key: 'fed_rates', label: 'Fed/Rates' },
  { key: 'inflation', label: 'Inflation' },
  { key: 'jobs', label: 'Jobs' },
  { key: 'energy', label: 'Energy' },
] as const;
type FilterKey = typeof FILTERS[number]['key'];

const _impactColor = (i: string | null | undefined) => {
  const u = (i || '').toUpperCase();
  if (u === 'HIGH') return 'bg-rose-500/10 text-rose-700 border-rose-500/30';
  if (u === 'MED' || u === 'MEDIUM') return 'bg-amber-500/10 text-amber-700 border-amber-500/30';
  if (u === 'LOW') return 'bg-slate-500/10 text-slate-700 border-slate-500/30';
  return 'bg-slate-500/10 text-slate-500 border-slate-500/30';
};

const _statusBadge = (status: string) => {
  if (status === 'live') return { color: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30', label: 'LIVE' };
  if (status === 'limited') return { color: 'bg-amber-500/10 text-amber-700 border-amber-500/30', label: 'LIMITED' };
  return { color: 'bg-slate-500/10 text-slate-700 border-slate-500/30', label: 'UNAVAILABLE' };
};

const EM_DASH = '—';

function _kwMatch(e: MacroEvent, words: string[]): boolean {
  const hay = ((e.event || '') + ' ' + (e.country || '') + ' ' + (e.strategy_relevance || '')).toLowerCase();
  return words.some((w) => hay.includes(w));
}

function _applyFilter(events: MacroEvent[], filter: FilterKey | null): MacroEvent[] {
  if (!filter) return events;
  const now = Date.now();
  const oneDayMs = 24 * 60 * 60 * 1000;
  switch (filter) {
    case 'today':
      return events.filter((e) => {
        const ts = e.time_utc ? Date.parse(e.time_utc.endsWith('Z') ? e.time_utc : e.time_utc + 'Z') : NaN;
        if (!Number.isFinite(ts)) return false;
        return Math.abs(ts - now) < oneDayMs;
      });
    case 'this_week':
      return events.filter((e) => {
        const ts = e.time_utc ? Date.parse(e.time_utc.endsWith('Z') ? e.time_utc : e.time_utc + 'Z') : NaN;
        if (!Number.isFinite(ts)) return false;
        return Math.abs(ts - now) < 7 * oneDayMs;
      });
    case 'high_impact':
      return events.filter((e) => (e.impact || '').toUpperCase() === 'HIGH');
    case 'fed_rates':
      return events.filter((e) => _kwMatch(e, ['fed', 'fomc', 'rate', 'interest']));
    case 'inflation':
      return events.filter((e) => _kwMatch(e, ['cpi', 'ppi', 'inflation', 'pce']));
    case 'jobs':
      return events.filter((e) => _kwMatch(e, ['jobs', 'employment', 'payroll', 'unemployment', 'nfp']));
    case 'energy':
      return events.filter((e) => _kwMatch(e, ['oil', 'opec', 'energy', 'eia', 'crude', 'gas']));
    default:
      return events;
  }
}

export default function MacroCalendarPanel() {
  const [payload, setPayload] = useState<MacroPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterKey | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(apiV1Base() + '/market/macro-calendar', { cache: 'no-store' });
      if (!r.ok) {
        setError('HTTP ' + r.status);
        setPayload(null);
      } else {
        const body = await r.json();
        const data = body?.data ?? body;
        setPayload(data as MacroPayload);
        setLastFetchedAt(Date.now());
      }
    } catch (e: any) {
      setError(String(e?.message || e));
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const status = payload?.status || 'unavailable';
  const statusBadge = _statusBadge(status);
  const allEvents = payload?.events || [];
  const filtered = useMemo(() => _applyFilter(allEvents, filter), [allEvents, filter]);

  return (
    <div data-testid="macro-calendar-panel" className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm w-full">
      {/* Header */}
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Calendar size={20} className="text-emerald-600" />
          <h3 className="text-lg font-black text-[var(--app-text)] uppercase italic tracking-tight">
            Economic & Macro Calendar
          </h3>
          <span data-testid="macro-status-badge" className={'inline-block px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest border ' + statusBadge.color}>
            {statusBadge.label}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="macro-refresh"
            onClick={() => void refresh()}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest border border-[var(--card-border)] bg-[var(--input-bg)] hover:bg-[var(--card-border)]/40 disabled:opacity-50"
            title="Refresh macro data"
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            {loading ? 'Refreshing…' : 'Refresh Macro Data'}
          </button>
        </div>
      </div>

      {/* Summary bar */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <div data-testid="macro-summary-regime" className="p-3 rounded-xl border border-[var(--card-border)] bg-[var(--input-bg)]">
          <div className="text-[9px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Macro Regime</div>
          <div className="text-sm font-black text-[var(--app-text)] italic uppercase">
            {payload?.regime ? payload.regime.replace('_', ' ') : EM_DASH}
          </div>
        </div>
        <div data-testid="macro-summary-next-event" className="p-3 rounded-xl border border-[var(--card-border)] bg-[var(--input-bg)]">
          <div className="text-[9px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Next Major Event</div>
          <div className="text-xs font-black text-[var(--app-text)]">
            {payload?.next_major_event?.event || EM_DASH}
          </div>
          <div className="text-[10px] font-bold text-[var(--muted-text)]">
            {safeFormatTimestamp(payload?.next_major_event?.time_utc, { mode: 'datetime', fallback: EM_DASH, invalidFallback: EM_DASH })}
          </div>
        </div>
        <div data-testid="macro-summary-highest-impact" className="p-3 rounded-xl border border-[var(--card-border)] bg-[var(--input-bg)]">
          <div className="text-[9px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Highest Impact</div>
          <div className="text-xs font-black text-[var(--app-text)]">
            {payload?.highest_impact_event?.event || EM_DASH}
          </div>
          <div className="text-[10px] font-bold text-[var(--muted-text)]">
            {(payload?.highest_impact_event?.impact || EM_DASH)}
          </div>
        </div>
        <div data-testid="macro-data-freshness" className="p-3 rounded-xl border border-[var(--card-border)] bg-[var(--input-bg)]">
          <div className="text-[9px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Data Freshness</div>
          <div className="text-xs font-black text-[var(--app-text)]">
            {payload?.last_updated_utc
              ? safeFormatTimestamp(payload.last_updated_utc, { mode: 'datetime', fallback: 'Never updated', invalidFallback: 'Update time unavailable' })
              : 'Never updated'}
          </div>
          <div className="text-[10px] font-bold text-[var(--muted-text)]">
            Source: {payload?.source || EM_DASH}
          </div>
        </div>
      </div>

      {/* Filter chips */}
      <div data-testid="macro-filter-chips" className="flex items-center gap-2 mb-4 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            data-testid={'macro-filter-' + f.key}
            onClick={() => setFilter((cur) => (cur === f.key ? null : f.key))}
            className={
              'px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border transition-colors ' +
              (filter === f.key
                ? 'bg-emerald-600 text-white border-emerald-600'
                : 'bg-[var(--input-bg)] text-[var(--muted-text)] border-[var(--card-border)] hover:text-[var(--app-text)]')
            }
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div data-testid="macro-error" className="mb-4 p-3 rounded-xl border border-rose-500/30 bg-rose-500/10 text-xs font-bold text-rose-700 flex items-center gap-2">
          <AlertCircle size={14} /> Refresh failed: {error}
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && filtered.length === 0 && (
        <div data-testid="macro-empty-state" className="rounded-xl border border-dashed border-[var(--card-border)] p-8 text-center">
          <Activity size={20} className="text-[var(--muted-text)] mx-auto mb-2 opacity-60" />
          <div className="text-sm font-black text-[var(--app-text)] uppercase italic mb-1">
            No verified macro calendar events available from the connected provider.
          </div>
          <div className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
            {payload?.reason_code ? 'Reason: ' + payload.reason_code : 'No fabricated events are shown.'}
          </div>
        </div>
      )}

      {/* Events table */}
      {!loading && !error && filtered.length > 0 && (
        <div className="w-full">
          <table data-testid="macro-events-table" className="w-full text-sm table-fixed">
            <thead>
              <tr className="border-b border-[var(--card-border)] text-[10px] font-black uppercase tracking-widest text-[var(--muted-text)] text-left">
                <th className="px-2 py-2 w-[14%]">Time</th>
                <th className="px-2 py-2 w-[24%]">Event</th>
                <th className="px-2 py-2 w-[8%]">Country</th>
                <th className="px-2 py-2 w-[8%]">Impact</th>
                <th className="px-2 py-2 w-[8%] hidden md:table-cell">Actual</th>
                <th className="px-2 py-2 w-[8%] hidden md:table-cell">Forecast</th>
                <th className="px-2 py-2 w-[8%] hidden md:table-cell">Previous</th>
                <th className="px-2 py-2 w-[22%]">Strategy relevance</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 50).map((e, i) => (
                <tr key={'macro-' + i} data-testid="macro-event-row" className="border-b border-[var(--card-border)] hover:bg-[var(--input-bg)]">
                  <td className="px-2 py-2 text-xs font-mono text-[var(--app-text)]">
                    {safeFormatTimestamp(e.time_utc, { mode: 'datetime', fallback: EM_DASH, invalidFallback: EM_DASH })}
                  </td>
                  <td className="px-2 py-2 text-xs font-bold text-[var(--app-text)]">{e.event || EM_DASH}</td>
                  <td className="px-2 py-2 text-[10px] font-bold text-[var(--muted-text)] uppercase">{e.country || EM_DASH}</td>
                  <td className="px-2 py-2">
                    <span className={'inline-block px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest border ' + _impactColor(e.impact)}>
                      {e.impact || EM_DASH}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-xs text-[var(--app-text)] hidden md:table-cell">{e.actual != null ? String(e.actual) : EM_DASH}</td>
                  <td className="px-2 py-2 text-xs text-[var(--muted-text)] hidden md:table-cell">{e.forecast != null ? String(e.forecast) : EM_DASH}</td>
                  <td className="px-2 py-2 text-xs text-[var(--muted-text)] hidden md:table-cell">{e.previous != null ? String(e.previous) : EM_DASH}</td>
                  <td className="px-2 py-2 text-xs text-[var(--muted-text)]">{e.strategy_relevance || EM_DASH}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-3 text-[10px] font-bold uppercase tracking-widest text-[var(--muted-text)]">
        Source: /api/v1/market/macro-calendar
        {lastFetchedAt ? ' · last fetched ' + safeFormatTimestamp(new Date(lastFetchedAt).toISOString(), { mode: 'time', fallback: '', invalidFallback: '' }) : ''}
      </div>
    </div>
  );
}
